/*
 * ------------------------------------------
 * File Name: l64-countdown.js
 * Description: Countdown timer for Latitude 64 product launches and slideshow banners.
 *              Uses a lightweight server-time offset so product cards, product pages,
 *              and hero banners stay aligned without adding any dependencies.
 * Author: Jahnavi Gottimukkala / Latitude 64 adaptation
 * Original Date Created: 2026-08-11
 * Updated: 2026-09-17 - hide product inventory + delivery ETA while product countdown is active
 * Version: 1.6
 * Updated: 2026-10-21 - Swedish day labels (dag/dagar) and release date line on /sv pages
 * ------------------------------------------
 *
 * Supported elements:
 *   - .product-single__coming-soon-timer[data-date]
 *   - .coming-soon-tag[data-date]
 *   - .hero__countdown-timer[data-date]
 *
 * data-date format:
 *   YYYY-MM-DD HH:mm:ss
 *
 * Product timers keep the existing UTC behavior.
 * Hero timers may use data-timezone="UTC" or "Europe/Stockholm".
 */

(function () {
  if (window.__L64CountdownInitialized) {
    window.dispatchEvent(new CustomEvent('l64:countdown:scan'));
    return;
  }
  window.__L64CountdownInitialized = true;

  const COUNTDOWN_SELECTOR = [
    '.product-single__coming-soon-timer[data-date]',
    '.coming-soon-tag[data-date]',
    '.hero__countdown-timer[data-date]'
  ].join(', ');

  let serverDatePromise = null;
  let observerStarted = false;

  const PRODUCT_GATING_STYLE_ID = 'l64-product-countdown-gating-styles';

  function ensureProductGatingStyles() {
    if (document.getElementById(PRODUCT_GATING_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = PRODUCT_GATING_STYLE_ID;
    style.textContent = `
      html.l64-product-countdown-active .icon-and-text:has([data-product-inventory]),
html.l64-product-countdown-active .essential-estimated-delivery-block-liquid,
html.l64-product-countdown-active .essential-estimated-delivery-card {
  display: none !important;
}
    `;
    document.head.appendChild(style);
  }

  function setProductCountdownGating(active) {
    ensureProductGatingStyles();
    document.documentElement.classList.toggle('l64-product-countdown-active', active);
  }

  async function fetchRealTime() {
    try {
      const response = await fetch('https://us-central1-curren-time.cloudfunctions.net/currentTime');
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const realTime = await response.json();
      return parseInt(realTime.data, 10);
    } catch (e) {
      console.log(e);
      return null;
    }
  }

  function getServerDate() {
    if (!serverDatePromise) {
      serverDatePromise = fetchRealTime().then((serverDate) => serverDate || Date.now());
    }
    return serverDatePromise;
  }

  function parseDateParts(value) {
    const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
    if (!match) return null;
    const [, y, mo, d, h, mi, s] = match.map(Number);
    return { y, mo, d, h, mi, s };
  }

  function parseDataDateAsUTC(value) {
    const p = parseDateParts(value);
    if (!p) return null;
    return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
  }

  // Convert a wall-clock time in an IANA zone (e.g. Europe/Stockholm) to UTC.
  // This keeps the editor friendly while still giving every visitor one shared launch moment.
  function parseDataDateInTimeZone(value, timeZone) {
    if (!timeZone || timeZone === 'UTC') return parseDataDateAsUTC(value);

    const p = parseDateParts(value);
    if (!p) return null;

    const wantedAsUTC = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
    let guess = wantedAsUTC;

    try {
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      });

      // Iterate because the timezone offset can change with daylight saving time.
      for (let i = 0; i < 3; i += 1) {
        const parts = Object.fromEntries(
          formatter.formatToParts(new Date(guess))
            .filter((part) => part.type !== 'literal')
            .map((part) => [part.type, Number(part.value)])
        );

        const representedAsUTC = Date.UTC(
          parts.year,
          parts.month - 1,
          parts.day,
          parts.hour,
          parts.minute,
          parts.second
        );

        const difference = wantedAsUTC - representedAsUTC;
        if (difference === 0) break;
        guess += difference;
      }

      return guess;
    } catch (e) {
      console.log(e);
      return parseDataDateAsUTC(value);
    }
  }

  function ordinalSuffix(day) {
    if (day % 10 === 1 && day % 100 !== 11) return 'st';
    if (day % 10 === 2 && day % 100 !== 12) return 'nd';
    if (day % 10 === 3 && day % 100 !== 13) return 'rd';
    return 'th';
  }

  // V1.6: Swedish pages (html lang="sv") get Swedish countdown words and release date.
  function isSwedishPage() {
    const lang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    return lang.indexOf('sv') === 0;
  }

  function dayLabel(days) {
    if (isSwedishPage()) return days === 1 ? 'dag' : 'dagar';
    return days === 1 ? 'day' : 'days';
  }

  function formatReleaseDate(launchMs) {
    const date = new Date(launchMs);
    if (isSwedishPage()) {
      try {
        return date.toLocaleString('sv-SE', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        // Fall through to the existing English format.
      }
    }
    const month = date.toLocaleString(undefined, { month: 'long' });
    const day = date.getDate();
    const year = date.getFullYear();
    const time = date.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${month} ${day}${ordinalSuffix(day)} ${year}, ${time.toLowerCase()}`;
  }

  function getCountdownParts(msRemaining) {
    const totalSeconds = Math.max(0, Math.floor(msRemaining / 1000));
    return {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60
    };
  }

  function formatCountdown(msRemaining) {
    const parts = getCountdownParts(msRemaining);
    const dayText = dayLabel(parts.days);
    const pad = (n) => String(n).padStart(2, '0');
    return `${parts.days} ${dayText} ${pad(parts.hours)}:${pad(parts.minutes)}:${pad(parts.seconds)}`;
  }

  // Update an existing text node instead of replacing child nodes every second.
  // This avoids unnecessary DOM mutations inside Flickity slides and keeps autoplay stable.
  function setTextWithoutStructureChange(element, value) {
    if (!element) return;
    if (element.childNodes.length === 1 && element.firstChild.nodeType === Node.TEXT_NODE) {
      element.firstChild.nodeValue = value;
    } else {
      element.textContent = value;
    }
  }

  function renderHeroCountdown(timerElement, msRemaining) {
    const parts = getCountdownParts(msRemaining);
    const pad = (n) => String(n).padStart(2, '0');

    const daysEl = timerElement.querySelector('[data-countdown-days]');
    const hoursEl = timerElement.querySelector('[data-countdown-hours]');
    const minutesEl = timerElement.querySelector('[data-countdown-minutes]');
    const secondsEl = timerElement.querySelector('[data-countdown-seconds]');

    if (daysEl && hoursEl && minutesEl && secondsEl) {
      setTextWithoutStructureChange(daysEl, pad(parts.days));
      setTextWithoutStructureChange(hoursEl, pad(parts.hours));
      setTextWithoutStructureChange(minutesEl, pad(parts.minutes));
      setTextWithoutStructureChange(secondsEl, pad(parts.seconds));
    } else {
      setTextWithoutStructureChange(timerElement, formatCountdown(msRemaining));
    }

    timerElement.setAttribute(
      'aria-label',
      `${parts.days} days, ${parts.hours} hours, ${parts.minutes} minutes, ${parts.seconds} seconds remaining`
    );
  }

  function setHeroLinksLocked(timerElement, locked) {
    const slide = timerElement.closest('.slideshow__slide');
    if (!slide || slide.getAttribute('data-countdown-lock-links') !== 'true') return;

    const links = slide.querySelectorAll('.hero__link a, .hero__slide-link');

    if (locked) {
      slide.classList.add('lat64-countdown-links-locked');
      links.forEach((link) => {
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      });
    } else {
      slide.classList.remove('lat64-countdown-links-locked');
      links.forEach((link) => {
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      });
    }
  }

  function expireHeroTimer(timerElement) {
    const container = timerElement.closest('.hero__countdown');
    const expiredText = timerElement.getAttribute('data-expired-text');

    setHeroLinksLocked(timerElement, false);

    if (container) container.classList.add('is-expired');

    if (expiredText) {
      // Replacing the four countdown units happens once, at expiry only.
      timerElement.textContent = expiredText;
      timerElement.classList.add('is-expired');
      timerElement.removeAttribute('aria-label');
    } else if (container) {
      container.style.display = 'none';
    }
  }

  function initializeTimer(timerElement, serverDate, type) {
    if (!timerElement || timerElement.dataset.comingSoonRunning === 'true') return;

    const dataDate = timerElement.getAttribute('data-date');
    const timeZone = type === 'hero' ? timerElement.getAttribute('data-timezone') : 'UTC';
    const launchMs = parseDataDateInTimeZone(dataDate, timeZone);
    if (!launchMs) return;

    timerElement.dataset.comingSoonRunning = 'true';

    const clientNow = Date.now();
    const clockOffset = serverDate - clientNow;
    let intervalId = null;

    // Product page only: while a visible countdown is still running, hide
    // the normal inventory status and Essential Estimated delivery widget.
    if (
      type === 'single' &&
      !timerElement.classList.contains('hide') &&
      launchMs > Date.now() + clockOffset
    ) {
      setProductCountdownGating(true);
    }

    if (type === 'single') {
      const timingEl = timerElement.parentElement
        ? timerElement.parentElement.querySelector('.product-single__coming-soon-timing')
        : null;
      if (timingEl) timingEl.textContent = (isSwedishPage() ? 'Släpps: ' : 'Release date: ') + formatReleaseDate(launchMs);
    } else if (type === 'hero') {
      setHeroLinksLocked(timerElement, true);
    }

    const tick = () => {
      const remaining = launchMs - (Date.now() + clockOffset);

      if (remaining <= 0) {
        if (intervalId) clearInterval(intervalId);
        timerElement.dataset.comingSoonRunning = 'false';

        if (type === 'single') {
          const container = timerElement.closest('.product-single__coming-soon');
          if (container) container.style.display = 'none';

          // Product is released: restore inventory status and delivery ETA.
          setProductCountdownGating(false);

          document.querySelectorAll('.coming-soon-product').forEach((el) => {
            el.classList.remove('coming-soon-product');
          });
        } else if (type === 'hero') {
          expireHeroTimer(timerElement);
        } else {
          timerElement.style.display = 'none';
        }
        return;
      }

      if (type === 'hero') {
        renderHeroCountdown(timerElement, remaining);
      } else {
        const countdownText = formatCountdown(remaining);
        if (type === 'single') {
          setTextWithoutStructureChange(timerElement, countdownText);
        } else {
          const countdownEl = timerElement.querySelector('.coming-soon-tag-countdown');
          if (countdownEl) setTextWithoutStructureChange(countdownEl, countdownText);
        }
      }
    };

    tick();
    if (launchMs > Date.now() + clockOffset) intervalId = setInterval(tick, 1000);
  }

  function getTimerType(element) {
    if (element.classList.contains('product-single__coming-soon-timer')) return 'single';
    if (element.classList.contains('hero__countdown-timer')) return 'hero';
    return 'grid';
  }

  function scanForCountdowns() {
    const uninitializedSelector = COUNTDOWN_SELECTOR
      .split(', ')
      .map((selector) => `${selector}:not([data-coming-soon-initialized])`)
      .join(', ');

    const elements = document.querySelectorAll(uninitializedSelector);
    if (!elements.length) return;

    getServerDate().then((serverDate) => {
      elements.forEach((element) => {
        element.setAttribute('data-coming-soon-initialized', 'true');
        initializeTimer(element, serverDate, getTimerType(element));
      });
    });

    startObserver();
  }

  function nodeContainsCountdown(node) {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
    if (node.matches && node.matches(COUNTDOWN_SELECTOR)) return true;
    return Boolean(node.querySelector && node.querySelector(COUNTDOWN_SELECTOR));
  }

  function startObserver() {
    if (observerStarted) return;
    observerStarted = true;

    const observer = new MutationObserver((mutations) => {
      // Ignore timer text changes. Only rescan when actual new elements are inserted.
      const hasNewCountdownContent = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(nodeContainsCountdown)
      );
      if (hasNewCountdownContent) scanForCountdowns();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initialScan() {
    if (!document.querySelector(COUNTDOWN_SELECTOR)) return;
    scanForCountdowns();
  }

  document.addEventListener('DOMContentLoaded', initialScan);

  document.addEventListener('shopify:section:load', () => {
    scanForCountdowns();
  });

  window.addEventListener('l64:countdown:scan', () => {
    scanForCountdowns();
  });
})();